import { lazy, Suspense, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import type { Variants } from "framer-motion";
import { Gavel, Globe, MicOff, Search } from "lucide-react";
import { useDebateStore } from "../../store/debateStore";
import { selectCurrentTurn, selectFocusSpeakerId } from "../../store/selectors";
import { usePersonaTheme } from "../../hooks/usePersonaTheme";
import { RoleIcon, roleLabel } from "../common/RoleIcon";
import type { Persona, Turn } from "../../domain/types";
import { cn } from "../common/cn";
import { t } from "../../i18n";
import { useResolvedAvatarUrl } from "./AvatarAllocationContext";

const FFTRadialVisualizer = lazy(() =>
  import("../audio/FFTRadialVisualizer").then((m) => ({ default: m.FFTRadialVisualizer })),
);


interface AgentAvatarProps {
  persona: Persona;
  size?: number;
  preview?: boolean;
  labelMaxWidth?: number;
  /**
   * Konsey Cognitive Renaissance — Bölüm D4/17.K.
   * Tıklanınca persona id ile çağrılır (BrainModal trigger).
   * Preview mode'da çağrılmaz (avatar tıklaması suppress edilir).
   */
  onPersonaClick?: (personaId: string) => void;
}

type AvatarState =
  | "idle"
  | "retrieving"
  | "critiquing"
  | "speaking"
  | "yielding"
  | "forfeited";

/**
 * Per-state base motion variants. Combined at runtime with an
 * **attention multiplier** so that the focused speaker visibly
 * dominates while everyone else fades into the background.
 *
 *   focused    →  scale ×1.06, opacity ×1.00  (spotlight)
 *   neutral    →  scale ×1.00, opacity ×1.00  (no-one is speaking yet)
 *   background →  scale ×0.90, opacity ×0.55  (someone else has the floor)
 *
 * The forfeited variant is special-cased — it stays grayscale and
 * dimmed regardless of attention so the dropout reads as inactive.
 */
const VARIANTS: Variants = {
  idle: { scale: 1, opacity: 1 },
  retrieving: { scale: 1.0, opacity: 0.95 },
  critiquing: { scale: 1.02, opacity: 1 },
  speaking: { scale: 1.14, opacity: 1 },
  yielding: { scale: 0.98, opacity: 0.85 },
  forfeited: { scale: 0.96, opacity: 0.5 },
};

const ATTENTION_MULTIPLIER: Record<
  "focused" | "neutral" | "background",
  { scale: number; opacity: number }
> = {
  focused: { scale: 1.06, opacity: 1.0 },
  neutral: { scale: 1.0, opacity: 1.0 },
  background: { scale: 0.9, opacity: 0.55 },
};

function deriveState(persona: Persona, turn: Turn | null, preview: boolean): AvatarState {
  if (preview) return "idle";
  if (!turn) return "idle";
  if (turn.speakerId !== persona.id) return "idle";
  if (turn.outcome === "forfeited") return "forfeited";
  return turn.phase as AvatarState;
}

const isForfeitedFlag = (s: AvatarState): boolean => s === "forfeited";

function initialsOf(displayName: string): string {
  return displayName
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function AgentAvatar({
  persona,
  size = 64,
  preview = false,
  labelMaxWidth,
  onPersonaClick,
}: AgentAvatarProps) {
  const reduceMotion = useReducedMotion();
  const turn = useDebateStore(selectCurrentTurn);
  const focusSpeakerId = useDebateStore(selectFocusSpeakerId);
  const themeStyle = usePersonaTheme(persona);
  const state = deriveState(persona, turn, preview);
  const [avatarErrored, setAvatarErrored] = useState(false);
  const avatarUrl = useResolvedAvatarUrl(persona);
  const showPhoto = avatarUrl.length > 0 && !avatarErrored;

  /* ──────────────────────────────────────────────────────────────────
     Attention class — drives the "speaker bright, others dim" effect.

     • preview              → neutral (wizard tile, no debate context)
     • forfeited            → neutral (already grayscale via class)
     • no focus speaker yet → neutral
     • focus is me          → focused (subtle scale-up + boosted halo)
     • focus is someone else → background (fade + desaturate)

     The multiplier is composed with the per-state base variant when
     animating, so e.g. a *speaking* persona ends up at 1.14 × 1.06 ≈
     1.21 scale (roughly 21 % bigger than the baseline disc).
     ────────────────────────────────────────────────────────────────── */
  const attention: "focused" | "neutral" | "background" = preview
    ? "neutral"
    : isForfeitedFlag(state)
      ? "neutral"
      : focusSpeakerId == null
        ? "neutral"
        : focusSpeakerId === persona.id
          ? "focused"
          : "background";

  const attentionMul = ATTENTION_MULTIPLIER[attention];

  const isSpeaking = state === "speaking";
  const isCritiquing = state === "critiquing";
  const isRetrieving = state === "retrieving";
  const isForfeited = state === "forfeited";
  const isIdle = state === "idle";

  const researchPending =
    isRetrieving && turn !== null && (!turn.research || turn.research.length === 0);
  const lastResearch = turn?.research?.[turn.research.length - 1];

  const ariaLabel = `${persona.displayName} (${roleLabel(persona.role)}); durum: ${
    preview
      ? "hazır"
      : state === "speaking"
        ? t("stage.speaking")
        : state === "retrieving"
          ? t("stage.retrieving")
          : state === "critiquing"
            ? t("stage.critiquing")
            : state === "forfeited"
              ? t("stage.forfeit")
              : "bekliyor"
  }`;

  // Konsey Cognitive Renaissance — Bölüm D4/17.K.
  // Brain modal trigger; preview mode'da disable. Hover/active hint
  // sadece live mode'da görünsün.
  const clickable = !preview && !!onPersonaClick;
  const handleAvatarClick = clickable
    ? () => onPersonaClick!(persona.id)
    : undefined;
  const handleKeyDown = clickable
    ? (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onPersonaClick!(persona.id);
        }
      }
    : undefined;

  return (
    <motion.div
      className={cn(
        "relative flex flex-col items-center justify-center text-center",
        "avatar-attention",
        attention === "focused" && "avatar-attention--focused",
        attention === "background" && "avatar-attention--background",
        isIdle && !preview && "hover:scale-[1.04] transition-transform duration-200",
        clickable && "cursor-pointer",
      )}
      style={themeStyle as Record<string, string | number>}
      // Compose the per-state base motion variant with the attention
      // multiplier so a speaking-and-focused persona reaches ~1.21
      // scale while a background persona shrinks to 0.9 and fades to
      // 55 % opacity. The forfeited state intentionally bypasses the
      // multiplier (its own dim/grayscale already conveys dropout).
      animate={{
        scale:
          ((VARIANTS[state] as { scale?: number })?.scale ?? 1) *
          attentionMul.scale,
        opacity:
          ((VARIANTS[state] as { opacity?: number })?.opacity ?? 1) *
          attentionMul.opacity,
      }}
      transition={
        reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 220, damping: 22 }
      }
      onClick={handleAvatarClick}
      onKeyDown={handleKeyDown}
      tabIndex={clickable ? 0 : -1}
      role={clickable ? "button" : undefined}
      aria-label={ariaLabel}
      title={clickable ? `${persona.displayName} — bilişsel profili göster` : undefined}
    >
      {/* === SPEAKING: Double-ring halo pulse + FFT Visualizer === */}
      {isSpeaking ? (
        <>
          {/* Inner ring */}
          <motion.span
            aria-hidden
            className="absolute rounded-full border-2 border-[hsl(var(--persona-glow)/0.8)]"
            style={{ width: size + 16, height: size + 16 }}
            animate={reduceMotion ? {} : { scale: [1, 1.06, 1], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
          {/* Outer ring */}
          <motion.span
            aria-hidden
            className="absolute rounded-full border border-[hsl(var(--persona-glow)/0.4)]"
            style={{ width: size + 28, height: size + 28 }}
            animate={reduceMotion ? {} : { scale: [1, 1.12, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
          />
          {/* FFT Radial Visualizer */}
          <Suspense fallback={null}>
            <FFTRadialVisualizer size={size + 36} />
          </Suspense>
        </>
      ) : null}

      {/* === RETRIEVING: Orbital dot === */}
      {isRetrieving ? (
        <>
          <span
            aria-hidden
            className="absolute rounded-full border border-dashed border-[hsl(var(--persona-glow)/0.5)]"
            style={{ width: size + 14, height: size + 14 }}
          />
          <span
            aria-hidden
            className="absolute"
            style={{
              width: size + 14,
              height: size + 14,
              animation: "orbital-dot 3s linear infinite",
              ["--orbital-radius" as string]: `${(size + 14) / 2}px`,
            }}
          >
            <span className="absolute top-1/2 left-0 h-2 w-2 -translate-y-1/2 rounded-full bg-[hsl(var(--persona-glow))] shadow-[0_0_6px_hsl(var(--persona-glow)/0.8)]" />
          </span>
        </>
      ) : null}

      {/* === IDLE: Subtle ambient ring === */}
      {(preview || isIdle) && !isForfeited && !isSpeaking && !isRetrieving && !isCritiquing ? (
        <motion.span
          aria-hidden
          className="absolute rounded-full border border-[hsl(var(--persona-glow)/0.3)]"
          style={{ width: size + 8, height: size + 8 }}
          animate={reduceMotion ? {} : { opacity: [0.2, 0.5, 0.2], scale: [1, 1.03, 1] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
        />
      ) : null}

      {/* === AVATAR FRAME WRAPPER ===
         Hosts the sci-fi HUD layers (breathe halo, counter-rotating
         conic rings, corner ticks, orbiting nodes). All layers are
         CSS-animated for zero JS overhead and fade out when the
         persona is forfeited. */}
      <div
        className="relative"
        style={{ width: size, height: size }}
      >
        {!isForfeited ? (
          <>
            {/* Outer breathing halo (always-on, low intensity) */}
            <span aria-hidden className="avatar-breathe" />
            {/* Counter-rotating conic gradient ring */}
            <span aria-hidden className="avatar-conic-ring" />
            {/* Inner counter-rotating conic (faster, reversed) */}
            <span aria-hidden className="avatar-conic-ring avatar-conic-ring--inner" />
            {/* HUD corner ticks rotating slowly around the disc */}
            <span aria-hidden className="avatar-hud-corners">
              <i /><i /><i /><i />
            </span>
            {/* Three orbital data nodes — staggered radii / speeds /
                phases so the dance never looks mechanical */}
            <span aria-hidden className="avatar-orbit-track">
              <span
                className="avatar-orbit-node"
                style={{
                  ["--avatar-orbit-r" as string]: `${size / 2 + 6}px`,
                  ["--avatar-orbit-duration" as string]: "7s",
                  ["--avatar-orbit-twinkle" as string]: "2.6s",
                  ["--avatar-orbit-delay" as string]: "0s",
                  ["--avatar-node-size" as string]: "5px",
                }}
              />
              <span
                className="avatar-orbit-node"
                style={{
                  ["--avatar-orbit-r" as string]: `${size / 2 + 10}px`,
                  ["--avatar-orbit-duration" as string]: "11s",
                  ["--avatar-orbit-twinkle" as string]: "1.8s",
                  ["--avatar-orbit-delay" as string]: "-2.2s",
                  ["--avatar-node-size" as string]: "3.5px",
                }}
              />
              <span
                className="avatar-orbit-node"
                style={{
                  ["--avatar-orbit-r" as string]: `${size / 2 + 4}px`,
                  ["--avatar-orbit-duration" as string]: "9s",
                  ["--avatar-orbit-twinkle" as string]: "3.1s",
                  ["--avatar-orbit-delay" as string]: "-4.5s",
                  ["--avatar-node-size" as string]: "4px",
                }}
              />
            </span>
          </>
        ) : null}

      {/* === AVATAR DISC (multi-layer gradient) === */}
      <div
        className={cn(
          "relative grid place-items-center rounded-full font-semibold text-white overflow-hidden",
          "border border-[hsl(var(--persona-glow)/0.6)]",
          isSpeaking && "ring-2 ring-[hsl(var(--persona-glow)/0.9)] ring-offset-2 ring-offset-[hsl(220_22%_6%)]",
          isCritiquing && "ring-1 ring-amber-300/60",
          isForfeited && "saturate-50 grayscale",
        )}
        style={{
          width: size,
          height: size,
          fontSize: size * 0.32,
          boxShadow: isSpeaking
            ? "var(--avatar-speaking-lift)"
            : "var(--avatar-lift-shadow)",
        }}
      >
        {/* Multi-layer gradient background */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(circle at 35% 25%, hsl(0 0% 100% / 0.15), transparent 50%),
              radial-gradient(circle at 50% 50%, hsl(var(--persona-glow) / 0.55), hsl(var(--persona-glow) / 0.2)),
              linear-gradient(180deg, hsl(var(--persona-glow) / 0.3), hsl(var(--persona-glow) / 0.05))
            `,
          }}
          aria-hidden
        />
        {/* Bottom vignette */}
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(180deg, transparent 50%, hsl(0 0% 0% / 0.3) 100%)",
          }}
          aria-hidden
        />
        {/* Diagonal shimmer pass — light scan across the disc.
            Clipped by the disc's own `overflow-hidden`. Skipped when
            forfeited so the dimmed avatar reads as inactive. */}
        {!isForfeited ? (
          <span
            aria-hidden
            className="avatar-shimmer"
            style={{
              ["--avatar-shimmer-delay" as string]: `${
                ((persona.seatIndex ?? 0) * 0.7) % 4.5
              }s`,
            }}
          />
        ) : null}
        {/* Content */}
        <span className="relative z-10 grid h-full w-full place-items-center">
          {showPhoto ? (
            <>
              <img
                src={avatarUrl}
                alt=""
                loading="lazy"
                decoding="async"
                onError={() => setAvatarErrored(true)}
                className="absolute inset-0 h-full w-full rounded-full object-cover"
                aria-hidden
              />
              {/* Phase overlays on top of the photo so retrieving/critiquing
                  visual cues remain visible without losing the persona face. */}
              {researchPending ? (
                <motion.span
                  aria-hidden
                  animate={reduceMotion ? {} : { rotate: [0, 360] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  className="relative z-10 grid place-items-center rounded-full bg-black/55 text-sky-100"
                  style={{ width: size * 0.55, height: size * 0.55 }}
                >
                  <Globe className="h-2/3 w-2/3 opacity-95" strokeWidth={1.5} />
                </motion.span>
              ) : isRetrieving ? (
                <span
                  aria-hidden
                  className="relative z-10 grid place-items-center rounded-full bg-black/50 text-sky-100"
                  style={{ width: size * 0.5, height: size * 0.5 }}
                >
                  <Search className="h-2/3 w-2/3 opacity-95" />
                </span>
              ) : null}
            </>
          ) : isSpeaking ? (
            <span aria-hidden className="text-[0.85em] tracking-wider drop-shadow-md">
              {initialsOf(persona.displayName)}
            </span>
          ) : researchPending ? (
            <motion.span
              aria-hidden
              animate={reduceMotion ? {} : { rotate: [0, 360] }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
              className="grid h-1/2 w-1/2 place-items-center text-sky-200"
              style={{ width: size * 0.5, height: size * 0.5 }}
            >
              <Globe className="h-full w-full opacity-90" strokeWidth={1.5} />
            </motion.span>
          ) : isRetrieving ? (
            <Search className="opacity-90" style={{ width: size * 0.4, height: size * 0.4 }} aria-hidden />
          ) : (
            <RoleIcon role={persona.role} className="opacity-95" style={{ width: size * 0.45, height: size * 0.45 }} aria-hidden />
          )}
        </span>
      </div>
      </div>{/* /AVATAR FRAME WRAPPER */}

      {/* === LABELS === */}
      <div
        className={cn(
          "mt-2 truncate text-xs font-semibold text-fg-strong drop-shadow-sm",
          labelMaxWidth === undefined && "max-w-[10rem]",
        )}
        style={labelMaxWidth !== undefined ? { maxWidth: labelMaxWidth } : undefined}
        title={persona.displayName}
      >
        {persona.displayName}
      </div>
      <div
        className={cn(
          "truncate text-[10px] font-medium uppercase tracking-wider text-fg-subtle/80",
          labelMaxWidth === undefined && "max-w-[12rem]",
        )}
        style={labelMaxWidth !== undefined ? { maxWidth: labelMaxWidth } : undefined}
        title={persona.expertise || roleLabel(persona.role)}
      >
        {persona.expertise?.trim() || roleLabel(persona.role)}
      </div>

      {/* Kalite Planı — moderatör koltuğu sahnede de "Oturum Başkanı"
          etiketi taşır (TranscriptTurn'daki pill ile aynı terim; küçük,
          accent-tonlu, seat label bütçesini bozmayan tek satır). */}
      {persona.role === "moderator" ? (
        <span
          className="mt-1 inline-flex items-center gap-1 rounded-full border border-[hsl(var(--accent)/0.45)] bg-[hsl(var(--accent)/0.12)] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-accent-300"
          title="Oturumu yöneten moderatör — konsensüs oylamasını yönetir."
        >
          <Gavel className="h-2.5 w-2.5" aria-hidden />
          Oturum Başkanı
        </span>
      ) : null}

      {/* Status indicators */}
      {researchPending ? (
        <div
          className={cn(
            "mt-1 truncate text-[10px] text-sky-200/90",
            labelMaxWidth === undefined && "max-w-[12rem]",
          )}
          style={labelMaxWidth !== undefined ? { maxWidth: labelMaxWidth } : undefined}
        >
          {t("stage.researching")}
          {lastResearch?.query ? `: ${lastResearch.query}` : ""}
        </div>
      ) : isRetrieving ? (
        <div className="mt-1 text-[10px] text-fg-subtle">{t("stage.retrieving")}</div>
      ) : null}
      {isCritiquing ? (
        <div className="mt-1 text-[10px] text-amber-200">{t("stage.critiquing")}</div>
      ) : null}
      {isForfeited ? (
        <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-dashed border-amber-400/50 bg-surface-2/80 px-2 py-0.5 text-[10px] uppercase tracking-wider text-amber-300">
          <MicOff className="h-2.5 w-2.5" aria-hidden />
          {t("stage.forfeit")}
        </span>
      ) : null}
    </motion.div>
  );
}
